import { FormControl } from '@angular/forms';

import { customFormGroup } from '../utilities';

/** Control name to the prefix it carries inside the flat `tags` array. */
export const SOURCE_TAG_GROUPS = {
  topic: 'topic',
  language: 'language',
  dataAvailable: 'data_available',
  characteristics: 'characteristics',
  partialTypes: 'partial_types',
  acceptedPaymentTypes: 'accepted_payment_types',
  other: 'other',
} as const;

export type SourceTagGroupKey = keyof typeof SOURCE_TAG_GROUPS;

export const SOURCE_TAG_GROUP_KEYS = Object.keys(
  SOURCE_TAG_GROUPS
) as SourceTagGroupKey[];

export const sourceTagGroupsForm = () =>
  customFormGroup(
    SOURCE_TAG_GROUP_KEYS.reduce(
      (controls, key) => {
        controls[key] = new FormControl<string[]>([], { nonNullable: true });
        return controls;
      },
      {} as Record<SourceTagGroupKey, FormControl<string[]>>
    )
  );

export const splitSourceTags = (
  tags: string[] = []
): Record<SourceTagGroupKey, string[]> => {
  const groups = SOURCE_TAG_GROUP_KEYS.reduce(
    (acc, key) => {
      acc[key] = [];
      return acc;
    },
    {} as Record<SourceTagGroupKey, string[]>
  );

  tags.forEach(tag => {
    const key = SOURCE_TAG_GROUP_KEYS.find(group =>
      tag.startsWith(`${SOURCE_TAG_GROUPS[group]}-`)
    );
    if (key) {
      groups[key].push(tag.slice(SOURCE_TAG_GROUPS[key].length + 1));
    }
  });

  return groups;
};

/**
 * Rebuilds the flat `tags` array. Values the record already had only get
 * lower-cased; new ones also have their first hyphen turned into a space, as
 * Dragonfly did when a tag was added. Tags with an unrecognised prefix are kept
 * as they were.
 */
export const joinSourceTags = (
  groups: Partial<Record<SourceTagGroupKey, string[] | null>>,
  originalTags: string[] = []
): string[] => {
  const grouped = SOURCE_TAG_GROUP_KEYS.flatMap(key => {
    const prefix = SOURCE_TAG_GROUPS[key];
    return (groups[key] ?? []).map(value => {
      const isSaved = originalTags.includes(`${prefix}-${value}`);
      const normalized = isSaved ? value : value.replace('-', ' ').trim();
      return `${prefix}-${normalized.toLowerCase()}`;
    });
  });

  const unknown = originalTags.filter(
    tag =>
      !SOURCE_TAG_GROUP_KEYS.some(key =>
        tag.startsWith(`${SOURCE_TAG_GROUPS[key]}-`)
      )
  );

  return [...grouped, ...unknown];
};

export type SourceTagGroupsFormGroup = ReturnType<typeof sourceTagGroupsForm>;
